import {css, StyleSheet} from 'aphrodite';
import React, {useState, KeyboardEvent, ChangeEvent, useRef, useCallback} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY, GRAY_DARKER, GRAY_LIGHTER, WHITE} from 'ui-library/Colors';

export interface DataCaptureChatInputControls {
  inputText: string;
  setInputText: (message: string) => void;
}

/**
 * Hook to manage chat input box state. In the future, this will support things
 * like file uploads.
 */
export function useDataCaptureChatInputControls(): DataCaptureChatInputControls {
  const [inputText, setInputText] = useState<string>('');
  return {inputText, setInputText};
}

export interface DataCaptureChatInputProps {
  inputText: string;
  setInputText: (message: string) => void;
  onSubmit: () => void;
  inputDisabled: boolean;
}

/**
 * Input box and send button for the user to send a message to the data capture agent.
 */
export function DataCaptureChatInput({
  inputText,
  setInputText,
  onSubmit,
  inputDisabled,
}: DataCaptureChatInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback((): void => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [textareaRef]);

  const handleMessageChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>): void => {
      setInputText(e.target.value);
      autoResize();
    },
    [autoResize, setInputText]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit]
  );

  const isSubmitDisabled = inputDisabled || !inputText.trim();

  return (
    <div className={css(styles.inputContainer)}>
      <div className={css(styles.inputGroup, inputDisabled && styles.inputGroupDisabled)}>
        <textarea
          ref={textareaRef}
          className={css(styles.input)}
          placeholder="Describe the data you'd like help capturing..."
          autoFocus={true}
          value={inputText}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          disabled={inputDisabled}
        />
        <div className={css(styles.inputGroupButtonRow)}>
          {/* Placeholder empty icon buttons for now. This is needed with the current styles
              to ensure the send button is right-aligned. */}
          <div className={css(styles.inputGroupIconButtons)} />
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            text="Send"
          />
        </div>
      </div>
      <div className={css(styles.warningMessage)}>AI can make mistakes. Always check results.</div>
    </div>
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    padding: '12px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${GRAY}`,
    borderRadius: `3px`,
    backgroundColor: WHITE,
    ':focus-within': {
      borderColor: BLUE_LIGHT,
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
  },
  input: {
    flexGrow: 1,
    width: '100%',
    minHeight: '80px',
    padding: '12px',
    fontFamily: 'inherit',
    fontSize: '14px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflow: 'hidden',
    ':focus': {
      outline: 'none',
    },
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  warningMessage: {
    fontSize: '12px',
    color: GRAY_DARKER,
    textAlign: 'center',
  },
});
